import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import type { OutreachInboxGroup } from 'fym-shared/src/music24/outreach/types';
import { TypedMemo } from '../../../common/types';
import type { InboxCounts, ReplyRow } from '../outreachTypes';
import { InboxRow } from './InboxRow';
import { INBOX_GROUP_TEXT } from './inboxGroups';
import { inboxMessages as messages } from './inboxMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Inbox.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface InboxGroupProps {
  group: OutreachInboxGroup;
  rows: ReplyRow[];
  count: number;
  /** The counts per label of every reply; null with a campaign filter (then the loaded rows count). */
  byLabel: InboxCounts['byLabel'] | null;
  openUuid: string | null;
  onOpen: (replyUuid: string) => void;
}

/**
 * One group of the inbox (design outreach-v2 m1): the head with its count, then the rows. "Hidden"
 * (out-of-office, bounce, unsubscribe) is collapsed by default and shows one summary line.
 */
const InboxGroupComponent: React.FC<InboxGroupProps> = ({ group, rows, count, byLabel, openUuid, onOpen }) => {
  const intl = useIntl();
  const collapsible = group === 'hidden';
  const [open, setOpen] = useState(!collapsible);
  const dim = group === 'done' || group === 'hidden';
  const items = useMemo(
    () =>
      rows.map((row) => (
        <InboxRow key={row.replyUuid} row={row} isOpen={row.replyUuid === openUuid} dim={dim} onOpen={onOpen} />
      )),
    [dim, onOpen, openUuid, rows]
  );
  const labels = useMemo(() => {
    if (byLabel !== null) {
      return byLabel;
    }
    const counted: InboxCounts['byLabel'] = {};
    rows.forEach((row) => {
      counted[row.label] = (counted[row.label] ?? 0) + 1;
    });
    return counted;
  }, [byLabel, rows]);
  if (group !== 'needsYou' && count === 0 && rows.length === 0) {
    return null;
  }
  const title = intl.formatMessage(INBOX_GROUP_TEXT[group]);
  const head = (
    <>
      <span>{title}</span>
      <span className={shared.grow} />
      <span data-testid={`inbox-group-count-${group}`}>{count}</span>
      {collapsible && <FontAwesomeIcon icon={faChevronDown} className={clsx(open && styles.groupChevronOpen)} />}
    </>
  );
  return (
    <div data-testid={`inbox-group-${group}`}>
      {collapsible ? (
        <button
          type="button"
          className={clsx(styles.groupHead, styles.groupHeadButton)}
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {head}
        </button>
      ) : (
        <div className={clsx(styles.groupHead, group === 'needsYou' && styles.groupHeadVio)}>{head}</div>
      )}
      {open && items}
      {open && group === 'needsYou' && rows.length === 0 && (
        <p className={clsx(shared.small, styles.groupEmpty)}>{intl.formatMessage(messages.nothingNeedsYou)}</p>
      )}
      {!open && (
        <button
          type="button"
          className={clsx(styles.item, styles.itemDim)}
          onClick={() => setOpen(true)}
          data-testid="inbox-hidden-summary"
        >
          <div className={shared.grow}>
            <div className={styles.itemName}>
              <b>
                {intl.formatMessage(messages.hiddenSummary, {
                  outOfOffice: labels.outOfOffice ?? 0,
                  bounces: labels.bounce ?? 0,
                  unsubscribes: labels.unsubscribe ?? 0,
                })}
              </b>
            </div>
            <div className={styles.itemSnippet}>{intl.formatMessage(messages.hiddenSub)}</div>
          </div>
        </button>
      )}
    </div>
  );
};

export const InboxGroup = TypedMemo(InboxGroupComponent);
